/**
 * WaitTimeSourceLabel — small, optional label showing the data source.
 * Non-breaking: purely additive UI component.
 */
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { WaitTimeSource } from '@/services/waitTimes';

interface WaitTimeSourceLabelProps {
  source?: WaitTimeSource;
  confidence?: number;
  disclaimer?: string;
  airportCode?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  tsa_gov: 'Live TSA data',
  airport_official: 'Live airport data',
  tsa_api: 'Live TSA data',
  estimate: 'Typical wait right now',
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatHour = (hour: number) => {
  const suffix = hour >= 12 ? 'pm' : 'am';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${suffix}`;
};

export const WaitTimeSourceLabel = ({ source, confidence, disclaimer, airportCode }: WaitTimeSourceLabelProps) => {
  if (!source) return null;

  const label = SOURCE_LABELS[source] || source;
  const isEstimate = source === 'estimate';

  const now = new Date();
  const patternNote = isEstimate
    ? `Based on ${airportCode ? `${airportCode}'s ` : ''}${DAY_NAMES[now.getDay()]} ${formatHour(now.getHours())} pattern`
    : null;

  return (
    <div className="flex items-center gap-1.5 mt-2">
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        isEstimate
          ? 'bg-muted text-muted-foreground'
          : 'bg-primary/10 text-primary'
      }`}>
        {label}
      </span>
      {patternNote && (
        <span className="text-xs text-muted-foreground hidden sm:inline">{patternNote}</span>
      )}
      {disclaimer && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs max-w-[200px]">{disclaimer}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
